import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import api from '../../api';
import {
  BarChart3,
  Calendar,
  AlertTriangle,
  Users,
  Files,
  TrendingDown,
  Cpu,
  ChevronRight,
  User,
  BookOpen,
  MessageSquare,
  ThumbsUp,
  Lightbulb,
  CheckCircle2,
  Target,
  Layout,
  GraduationCap,
  Layers,
  Activity,
  Check,
  X,
  Loader2,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { canAccess } from '../../lib/rbac';

function DashboardSkeleton() {
  return (
    <div className="flex flex-col h-full bg-zinc-50/50 dark:bg-zinc-950/50">
      <div className="h-24 px-8 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex items-center justify-between sticky top-0 z-10">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="hidden xl:flex items-center gap-10">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="border-l border-dashed border-zinc-200 dark:border-zinc-800 pl-5 h-10 flex flex-col justify-center gap-2">
              <Skeleton className="h-2 w-12" />
              <Skeleton className="h-5 w-8" />
            </div>
          ))}
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 md:p-6 space-y-6 w-full">
          {/* KPI Skeleton moved to Header */}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {/* Scheduling Chart Skeleton */}
            <div className="xl:col-span-2 h-[480px] p-6 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm">
              <div className="flex justify-between mb-8">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-7 w-32" />
              </div>
              <div className="flex items-end gap-4 h-64">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="flex-1" style={{ height: `${Math.random() * 60 + 20}%` }} />
                ))}
              </div>
            </div>

            {/* Student Detection Skeleton */}
            <div className="xl:col-span-1 h-[480px] p-6 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm">
              <Skeleton className="h-5 w-40 mb-6" />
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex gap-3">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-2 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            </div>



            {/* Faculty Allotment Skeleton */}
            <div className="xl:col-span-1 h-[480px] p-6 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm">
              <Skeleton className="h-5 w-40 mb-6" />
              <div className="space-y-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-2 w-20" />
                    <Skeleton className="h-1.5 w-full" />
                  </div>
                ))}
              </div>
            </div>

            {/* Inquiry Stats Skeleton */}
            <div className="xl:col-span-4 h-[350px] p-8 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm">
              <Skeleton className="h-5 w-40 mb-6" />
              <div className="flex items-end justify-around gap-8 h-56">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="flex-1 max-w-[100px]" style={{ height: `${Math.random() * 50 + 30}%` }} />
                ))}
              </div>
            </div>

            <div className="flex-1 min-w-[300px] h-[300px] p-5 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm">
              <Skeleton className="h-5 w-40 mb-6" />
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex gap-3 p-2">
                    <Skeleton className="w-7 h-7 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-3 w-1/2" />
                      <Skeleton className="h-2 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

const DEFAULT_KPI = [
  { label: 'Courses', value: '0', icon: GraduationCap, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { label: 'Subjects', value: '0', icon: BookOpen, color: 'text-violet-500', bg: 'bg-violet-500/10' },
  { label: 'Students', value: '0', icon: Users, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  { label: 'Batches', value: '0', icon: Layers, color: 'text-zinc-500', bg: 'bg-zinc-500/10' },
];

// No hardcoded subject hours defaults


function ProgressRow({ label, value, color, pct, trackPct = 100 }) {
  return (
    <div className="space-y-1.5 w-full">
      <div className="flex justify-between text-[11px] font-medium">
        <span className="text-zinc-500 truncate pr-2 max-w-[70%]">{label}</span>
        <span className="text-zinc-700 dark:text-zinc-300 shrink-0">{value}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden transition-all duration-500" style={{ width: `${trackPct}%` }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        </div>
        {trackPct < 100 && (
          <div className="flex-1 h-[1px] bg-zinc-100 dark:bg-zinc-800/50 dashed" />
        )}
      </div>
    </div>
  );
}

export default function MainDashboardPanel() {
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [kpiCards, setKpiCards] = useState(DEFAULT_KPI);
  const [passwordRequests, setPasswordRequests] = useState([]);

  const profileStr = localStorage.getItem('admin_profile');
  const profile = profileStr ? JSON.parse(profileStr) : {};

  const handleApprovePassword = async (reqId) => {
    try {
      await api.post(`/api/admin/password-requests/${reqId}/approve`);
      toast.success('Password reset allowed. User can now set up a new password.');
      setPasswordRequests(prev => prev.filter(r => r.id !== reqId));
    } catch (err) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectPassword = async (reqId) => {
    try {
      await api.post(`/api/admin/password-requests/${reqId}/reject`);
      toast.success('Request dismissed.');
      setPasswordRequests(prev => prev.filter(r => r.id !== reqId));
    } catch (err) {
      toast.error('Failed to reject request');
    }
  };
  const [studentsAtRisk, setStudentsAtRisk] = useState([]);
  const [subjectHours, setSubjectHours] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [studentStats, setStudentStats] = useState({ total: 0, atRisk: 0, attendanceAvg: 0 });
  const [leastScoringTopics, setLeastScoringTopics] = useState([]);
  const [allotmentHorizon, setAllotmentHorizon] = useState('week'); // 'week', 'month', 'weekly_avg', 'total'
  const [rawFacultyAssignments, setRawFacultyAssignments] = useState([]);
  const [rawScheduledEvents, setRawScheduledEvents] = useState([]);
  const [inquiryStats, setInquiryStats] = useState({ tag_counts: {}, recent: [] });
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    courses: 0,
    subjects: 0,
    students: 0,
    batches: 0,
    activeTemplates: 0,
    faculty: 0,
    branches: 0
  });

  const [systemHealth, setSystemHealth] = useState([]);
  const [isHealthOpen, setIsHealthOpen] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);

  const fetchSystemHealth = async () => {
    try {
      setHealthLoading(true);
      const res = await api.get('/api/system/health');
      setSystemHealth(res.data || []);
    } catch (err) {
      console.error("Health check failed:", err);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemHealth();
    const interval = setInterval(fetchSystemHealth, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    async function fetchDashboardData() {
      if (canAccess(1)) {
        api.get('/api/admin/password-requests')
          .then(res => setPasswordRequests(res.data))
          .catch(err => console.error('Error fetching password requests:', err));
      }
      const teamId = profile.team_id;

      if (!teamId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [
          coursesRes,
          subjectsRes,
          studentsRes,
          batchesRes,
          facultyRes,
          branchesRes,
          facultyAssignmentsRes,
          scheduledEventsRes,
          inquiryRecentRes,
          inquiryTagsRes
        ] = await Promise.all([
          supabase.from('courses').select('id, name').eq('team_id', teamId),
          supabase.from('subjects').select('id, name, course_id').eq('team_id', teamId),
          supabase.from('students').select('id, name, attendance_pct, overall_score, performance_data, batch_id').eq('team_id', teamId),
          supabase.from('batches').select('id, course_id').eq('team_id', teamId),
          supabase.from('faculty').select('id', { count: 'exact', head: true }).eq('team_id', teamId),
          supabase.from('branches').select('id', { count: 'exact', head: true }).eq('team_id', teamId),
          supabase.from('faculty_assignments').select(`*, faculty:faculty_id(name, max_hours_per_day, status)`).eq('team_id', teamId),
          supabase.from('scheduled_events').select('assignment_id, start_time, end_time').eq('event_type', 'lecture').eq('team_id', teamId),
          supabase.from('inquiries').select('*').eq('team_id', teamId).order('created_at', { ascending: false }).limit(5),
          supabase.from('inquiries').select('tag').eq('team_id', teamId)
        ]);

        const coursesData = coursesRes.data;
        const subjectsData = subjectsRes.data;
        const studentsRaw = studentsRes.data;
        const facultyAssignments = facultyAssignmentsRes.data;
        const scheduledEvents = scheduledEventsRes.data;
        const inquiryRecent = inquiryRecentRes.data;
        const inquiryTagsRows = inquiryTagsRes.data;
        const activeTemplates = 0; // Default or fetch if needed
        const batchesData = batchesRes.data;

        // Manually join students with batches for course_id
        const allStudents = studentsRaw?.map(s => {
          const batch = batchesData?.find(b => b.id === s.batch_id);
          return { ...s, batch };
        }) || [];

        // 1. Process Scheduling Metrics (Replica of backend/algorithms/dashboard_cache.py)
        const activeFA = (facultyAssignments || []).filter(fa => fa.faculty?.status === 'active');
        const assignmentsWithActuals = activeFA.map(asg => {
          const events = (scheduledEvents || []).filter(e => e.assignment_id === asg.id);
          const totalHours = events.reduce((sum, e) => {
            const start = new Date(e.start_time);
            const end = new Date(e.end_time);
            const duration = (end - start) / (1000 * 3600);
            return sum + duration;
          }, 0);
          return { ...asg, actualHours: totalHours };
        });

        const coverageRatios = assignmentsWithActuals.map(asg =>
          Math.min(asg.actualHours / Math.max(1, asg.hours_per_week || 3), 1.0)
        );
        const coveragePct = assignmentsWithActuals.length > 0
          ? (coverageRatios.reduce((a, b) => a + b, 0) / coverageRatios.length) * 100
          : 0;
        const unscheduledCount = assignmentsWithActuals.filter(asg => asg.actualHours === 0).length;

        const scheduling = {
          total_assignments: activeFA.length,
          scheduled_events: (scheduledEvents || []).length,
          active_templates: activeTemplates || 0,
          coverage_pct: coveragePct,
          unscheduled_count: unscheduledCount
        };

        // 2. Process Inquiry Stats (Replica of backend/server.py get_inquiry_stats)
        const tagCountsMap = { positive: 0, convincing: 0, completed: 0, inquiry: 0, rejected: 0 };
        (inquiryTagsRows || []).forEach(row => {
          if (tagCountsMap[row.tag] !== undefined) tagCountsMap[row.tag]++;
        });

        setInquiryStats({
          tag_counts: tagCountsMap,
          recent: inquiryRecent || []
        });

        // 3. Update KPI Cards
        setKpiCards([
          { label: 'Total Students', value: allStudents?.length.toString() || '0', icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10', path: '/dashboard/students' },
          { label: 'Assignments', value: scheduling.total_assignments?.toString() || '0', icon: Target, color: 'text-indigo-500', bg: 'bg-indigo-500/10', path: '/dashboard/assignments' },
          { label: 'Scheduled', value: scheduling.scheduled_events?.toString() || '0', icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10', path: '/dashboard/calendar' },
          { label: 'Inquiries', value: inquiryTagsRows?.length.toString() || '0', icon: MessageSquare, color: 'text-amber-500', bg: 'bg-amber-500/10', path: '/dashboard/inquiries' },
        ]);

        // 4. Update Courses List
        if (coursesData) {
          setCoursesList(coursesData.map(c => ({ id: c.id, name: c.name })));
        }

        // 5. Performance Analysis (Least Scoring Topics & Dropout Risk)
        if (allStudents) {
          // Topics Analysis
          const topicStats = {};
          (allStudents || []).forEach(student => {
            const history = student.performance_data || [];
            history.forEach(mark => {
              const label = mark.test_name;
              if (!label) return;
              if (!topicStats[label]) topicStats[label] = { sum: 0, count: 0 };
              const total = mark.total_marks || 100;
              topicStats[label].sum += (mark.score / total) * 100;
              topicStats[label].count += 1;
            });
          });
          const sortedTopics = Object.keys(topicStats)
            .map(title => ({ title, avg: topicStats[title].sum / topicStats[title].count }))
            .sort((a, b) => a.avg - b.avg);
          setLeastScoringTopics(sortedTopics.slice(0, 2).map(t => t.title));

          // At Risk Analysis
          if (allStudents) {
            const studentsWithRisk = allStudents.map(student => {
              const avgScore = student.overall_score || 0;
              const attendance = student.attendance_pct || 0;
              const collectivePct = (avgScore + attendance) / 2;
              return { ...student, avgScore, collectivePct };
            });

            const filteredStudents = selectedCourse === 'all'
              ? studentsWithRisk
              : studentsWithRisk.filter(s => s.batch?.course_id === selectedCourse);

            setStudentsAtRisk(filteredStudents
              .sort((a, b) => a.collectivePct - b.collectivePct)
              .slice(0, 5)
              .map(s => ({
                id: s.id,
                name: s.name,
                grade: `${Math.round(s.avgScore)}%`,
                attendance: `${Math.round(s.attendance_pct)}%`,
                collective: `${Math.round(s.collectivePct)}%`,
                subject: 'Overall',
                topic: `Collective Score: ${Math.round(s.collectivePct)}%`
              }))
            );
          }
        }

        // 6. Subject & Faculty Hours Aggregation
        if (subjectsData) {
          const filteredSubjects = selectedCourse === 'all'
            ? subjectsData
            : subjectsData.filter(s => s.course_id === selectedCourse);

          const hoursMap = {};
          const facultyMap = {};
          const subjectColors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4'];

          // Initialize faculty map with assignmentsWithActuals for better accuracy
          assignmentsWithActuals.forEach(awa => {
            const facultyName = awa.faculty?.name || 'Unassigned';
            if (!facultyMap[facultyName]) {
              facultyMap[facultyName] = { allotted: 0, actual: 0 };
            }
            facultyMap[facultyName].allotted += (awa.hours_per_week || 0);
            facultyMap[facultyName].actual += (awa.actualHours || 0);

            // Only aggregate for currently visible subjects
            if (filteredSubjects.some(s => s.id === awa.subject_id)) {
              hoursMap[awa.subject_id] = (hoursMap[awa.subject_id] || 0) + (awa.actualHours || 0);
            }
          });

          setSubjectHours(filteredSubjects.map((s, i) => ({
            id: s.id,
            subject: s.name,
            hours: hoursMap[s.id] || 0,
            color: subjectColors[i % subjectColors.length]
          })));
        }

        setRawFacultyAssignments(facultyAssignments || []);
        setRawScheduledEvents(scheduledEvents || []);
      } catch (err) {
        console.error('Error calculating dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchDashboardData();
  }, [selectedCourse]);

  const facultyHours = useMemo(() => {
    const activeFA = rawFacultyAssignments.filter(fa => fa.faculty?.status === 'active');
    const facultyMap = {};

    const now = new Date();

    // Start & end of current week (Monday to Sunday)
    const currentDay = now.getDay();
    const distanceToMonday = currentDay === 0 ? -6 : 1 - currentDay;
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() + distanceToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    // Start & end of current month
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const daysInMonth = endOfMonth.getDate();
    const weeksInMonth = daysInMonth / 7;

    // Total weeks based on all scheduled events
    let totalWeeks = 1;
    if (rawScheduledEvents.length > 0) {
      const times = rawScheduledEvents
        .map(e => new Date(e.start_time).getTime())
        .filter(t => !isNaN(t));
      if (times.length > 0) {
        const minTime = Math.min(...times);
        const maxTime = Math.max(...times);
        const diffMs = maxTime - minTime;
        totalWeeks = Math.max(1, diffMs / (7 * 24 * 3600 * 1000));
      }
    }

    // Process each faculty
    activeFA.forEach(asg => {
      const facultyName = asg.faculty?.name || 'Unassigned';
      if (!facultyMap[facultyName]) {
        facultyMap[facultyName] = { allottedWeekly: 0, actualWeekly: 0, actualMonth: 0, actualTotal: 0 };
      }

      const baseAllotted = asg.hours_per_week || 0;
      facultyMap[facultyName].allottedWeekly += baseAllotted;

      const events = rawScheduledEvents.filter(e => e.assignment_id === asg.id);

      events.forEach(e => {
        const start = new Date(e.start_time);
        const end = new Date(e.end_time);
        const duration = (end - start) / (1000 * 3600);
        if (isNaN(duration)) return;

        facultyMap[facultyName].actualTotal += duration;

        if (start >= startOfWeek && start <= endOfWeek) {
          facultyMap[facultyName].actualWeekly += duration;
        }

        if (start >= startOfMonth && start <= endOfMonth) {
          facultyMap[facultyName].actualMonth += duration;
        }
      });
    });

    return Object.keys(facultyMap).map(name => {
      const { allottedWeekly, actualWeekly, actualMonth, actualTotal } = facultyMap[name];
      let allotted = allottedWeekly;
      let actual = actualWeekly;

      if (allotmentHorizon === 'week') {
        allotted = allottedWeekly;
        actual = actualWeekly;
      } else if (allotmentHorizon === 'month') {
        allotted = allottedWeekly * weeksInMonth;
        actual = actualMonth;
      } else if (allotmentHorizon === 'weekly_avg') {
        allotted = allottedWeekly;
        actual = actualTotal / totalWeeks;
      } else if (allotmentHorizon === 'total') {
        allotted = allottedWeekly * totalWeeks;
        actual = actualTotal;
      }

      return {
        name,
        allotted,
        actual,
        pct: Math.min((actual / Math.max(1, allotted)) * 100, 100)
      };
    }).sort((a, b) => b.allotted - a.allotted);
  }, [rawFacultyAssignments, rawScheduledEvents, allotmentHorizon]);

  if (loading && coursesList.length === 0) {
    return <DashboardSkeleton />;
  }


  return (
    <div className="flex flex-col h-full bg-zinc-50/50 dark:bg-zinc-950/50" data-testid="main-dashboard">
      {/* Dashboard Top Bar */}
      <header className="h-16 md:h-24 border-b border-zinc-200 dark:border-zinc-800 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl flex items-center justify-between px-4 md:px-8 sticky top-0 z-50">
        <div className="flex items-center gap-4 md:gap-6">
          <div className="flex flex-col gap-0.5">
            <h2 className="font-display text-lg md:text-2xl font-black tracking-tighter dark:text-white uppercase leading-none">
              Overview
            </h2>
            <p className="hidden md:block text-[11px] text-zinc-500 font-bold uppercase tracking-[0.15em] opacity-80">
              Real-time management dashboard
            </p>
          </div>

          {/* Health Status Dropdown */}
          <div className="relative pt-1">
            <button
              onClick={() => setIsHealthOpen(!isHealthOpen)}
              className={cn(
                "flex items-center gap-1.5 md:gap-2.5 px-2 md:px-3 py-1 md:py-1.5 rounded-lg md:rounded-xl transition-all border shadow-sm",
                isHealthOpen
                  ? "bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 shadow-md"
                  : "bg-zinc-50/50 dark:bg-zinc-800/30 border-zinc-200/50 dark:border-zinc-800/50 hover:bg-white dark:hover:bg-zinc-800"
              )}
            >
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className={cn(
                  "w-1.5 h-1.5 rounded-full animate-pulse",
                  systemHealth.every(s => s.status === 'ok') ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]" : "bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.4)]"
                )} />
                <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                  <span className="hidden sm:inline">System Infrastructure</span>
                  <span className="sm:hidden">Status</span>
                </span>
              </div>
              <ChevronDown className={cn("w-3 h-3 md:w-3.5 md:h-3.5 text-zinc-400 transition-transform duration-500", isHealthOpen && "rotate-180")} />
            </button>

            <AnimatePresence>
              {isHealthOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute top-full mt-2 left-0 w-72 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl shadow-2xl p-4 z-[100] backdrop-blur-xl"
                >
                  <div className="flex items-center justify-between mb-4 px-2">
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400">Node Status</span>
                    <button
                      onClick={fetchSystemHealth}
                      className="text-[9px] font-bold text-blue-500 uppercase hover:underline"
                    >
                      Refresh
                    </button>
                  </div>
                  <div className="space-y-2">
                    {systemHealth.map((service, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100/50 dark:border-zinc-700/50">
                        <div className="shrink-0">
                          {healthLoading ? (
                            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                          ) : service.status === 'ok' ? (
                            <div className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                              <Check className="w-3 h-3 text-green-500" />
                            </div>
                          ) : (
                            <div className="w-4 h-4 rounded-full bg-red-500/10 flex items-center justify-center">
                              <X className="w-3 h-3 text-red-500" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200 truncate leading-none mb-1">
                            {service.name}
                          </p>
                          <p className="text-[9px] text-zinc-500 truncate leading-none">
                            {service.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>


        <div className="flex items-center gap-6">
          <div className="hidden xl:flex items-center gap-8">
            {kpiCards.map((kpi, i) => (
              <div 
                key={i} 
                className="flex flex-col items-start border-l border-dashed border-zinc-300 dark:border-zinc-700 pl-5 h-10 justify-center cursor-pointer hover:border-blue-500 transition-colors group"
                onClick={() => kpi.path && navigate(kpi.path)}
              >
                <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-0.5 leading-none group-hover:text-zinc-600 dark:group-hover:text-zinc-300">{kpi.label}</span>
                <span className={cn("text-xl font-black tracking-tighter leading-none", kpi.color)}>
                  {kpi.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </header>

      <ScrollArea className="flex-1">
        <div className="p-4 md:p-6 space-y-6 w-full">
          {/* Mobile KPIs - Horizontal Scroll for premium feel */}
          <div className="xl:hidden -mx-4 px-4 overflow-x-auto no-scrollbar pb-2">
            <div className="flex items-center gap-4 min-w-max">
              {kpiCards.map((kpi, i) => (
                <div 
                  key={i} 
                  className="flex flex-col items-start bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 w-40 shadow-sm active:scale-95 transition-all"
                  onClick={() => kpi.path && navigate(kpi.path)}
                >
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center mb-3", kpi.bg)}>
                    <kpi.icon className={cn("w-4 h-4", kpi.color)} />
                  </div>
                  <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-0.5 leading-none">{kpi.label}</span>
                  <span className={cn("text-xl font-black tracking-tighter leading-none", kpi.color)}>
                    {kpi.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {canAccess(1) && passwordRequests.length > 0 && (
            <div className="bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 rounded-2xl p-4 shadow-sm animate-fade-in">
              <div className="flex items-center gap-2 mb-3 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <h3 className="text-xs font-black uppercase tracking-wider">Pending Password Resets</h3>
              </div>
              <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {passwordRequests.map(req => (
                  <div key={req.id} className="py-2.5 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-bold text-zinc-800 dark:text-zinc-100">{req.name || req.username}</p>
                      <p className="text-[9px] text-zinc-400">{req.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprovePassword(req.id)}
                        className="px-2.5 py-1 text-[9px] font-bold bg-amber-500 hover:bg-amber-600 text-white rounded-md transition-colors shadow-sm"
                      >
                        Allow Reset
                      </button>
                      <button
                        onClick={() => handleRejectPassword(req.id)}
                        className="px-2.5 py-1 text-[9px] font-bold bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-400 rounded-md transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KPIs moved to Header */}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">

            {/* Scheduling Chart - Takes more space if available */}
            <div className="xl:col-span-2 p-6 h-[480px] rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm flex flex-col transition-all hover:shadow-md">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-bold">Scheduling: All Subjects vs Hours</h3>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedCourse || 'all'}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="text-[10px] h-7 w-28 md:w-32 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-2 pr-6 focus:outline-none focus:ring-2 focus:ring-blue-500/10 transition-all cursor-pointer font-bold"
                  >
                    <option value="all">All Courses</option>
                    {coursesList.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Max and Y-axis scale */}
              {(() => {
                const weeklyFacultyAllotted = rawFacultyAssignments.reduce((acc, fa) => {
                  if (fa.faculty?.status === 'active') {
                    const name = fa.faculty.name || 'Unassigned';
                    acc[name] = (acc[name] || 0) + (fa.hours_per_week || 0);
                  }
                  return acc;
                }, {});
                const globalMax = Math.max(
                  ...Object.values(weeklyFacultyAllotted),
                  ...subjectHours.map(sh => sh.hours),
                  1
                );
                const chartMax = globalMax < 5 ? 5 : globalMax;
                const yTicks = [chartMax, chartMax * 0.75, chartMax * 0.5, chartMax * 0.25, 0];

                return (
                  <div className="flex-1 flex gap-4 h-full min-h-0">
                    {/* Y-Axis Ticks */}
                    <div className="flex flex-col justify-between h-full pb-2 text-[9px] text-zinc-400 font-bold border-r border-zinc-100 dark:border-zinc-800/50 pr-3 tabular-nums shrink-0">
                      {yTicks.map((tick, i) => (
                        <span key={i} className="flex items-center justify-end gap-1">
                          {Math.round(tick)}h
                          <div className="w-1 h-[1px] bg-zinc-200 dark:bg-zinc-700" />
                        </span>
                      ))}
                    </div>

                    {/* Chart Area */}
                    <div className="flex-1 min-w-0 flex items-end justify-between gap-1 sm:gap-2 h-full px-2 border-b border-zinc-100 dark:border-zinc-800 pb-2 relative">
                      {/* Grid Lines */}
                      <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-2 pr-2">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="w-full border-t border-zinc-50 dark:border-zinc-800/30 border-dashed" />
                        ))}
                      </div>

                      {subjectHours.length > 0 ? (
                        subjectHours.map((sh, i) => (
                          <div key={i} className="flex-1 min-w-0 flex flex-col items-center gap-3 group relative h-full justify-end">
                            <div
                              className="w-full max-w-[40px] rounded-t-lg transition-all duration-500 cursor-pointer group-hover:opacity-80 min-h-[2px] z-10"
                              style={{
                                height: `${(sh.hours / chartMax) * 100}%`,
                                backgroundColor: sh.color,
                                boxShadow: `0 4px 12px -2px ${sh.color}44`
                              }}
                            />
                            <div className="absolute -top-6 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-zinc-900 border border-zinc-700 text-white text-[10px] px-2 py-1 rounded-md shadow-2xl pointer-events-none z-30">
                              <span className="font-bold">{sh.hours}</span> <span className="opacity-70 text-[8px]">HOURS</span>
                            </div>
                            <span className="text-[10px] font-bold text-zinc-400 group-hover:text-zinc-700 dark:group-hover:text-zinc-200 transition-colors truncate w-full text-center block">
                              {sh.subject}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400 italic">
                          <BarChart3 className="w-8 h-8 mb-2 opacity-20" />
                          <p className="text-[10px]">No subject hours to display</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4 flex justify-center gap-6">
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                  <div className="w-2 h-2 rounded-full bg-blue-500" /> Planned
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                  <div className="w-2 h-2 rounded-full bg-zinc-200 dark:bg-zinc-700" /> Max Cap
                </div>
              </div>
            </div>

            {/* Student Detection - Takes available space */}
            <div className="xl:col-span-1 p-6 h-[480px] rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm flex flex-col transition-all hover:shadow-md">
              <div className="flex items-center gap-2 mb-6">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-bold">Early Dropout Detection</h3>
              </div>

              <div className="space-y-3 flex-1 overflow-y-auto max-h-[400px] pr-1">
                {studentsAtRisk.map((student) => (
                  <div
                    key={student.id}
                    className="p-3 rounded-lg border border-zinc-100 dark:border-zinc-800 hover:border-red-200 dark:hover:border-red-900/40 bg-zinc-50/50 dark:bg-zinc-800/30 transition-all cursor-pointer group"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-zinc-500" />
                        </div>
                        <div>
                          <p className="text-[11px] font-bold group-hover:text-red-500 transition-colors">{student.name}</p>
                          <p className="text-[9px] text-zinc-400">{student.subject} Grade: {student.grade}</p>
                        </div>
                      </div>
                      <Badge className="text-[9px] bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-100 font-bold px-1.5 py-0">At Risk</Badge>
                    </div>
                    <div className="flex items-center justify-between text-[9px] text-zinc-500">

                      <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => window.location.href = '/dashboard/students'}
                className="mt-4 w-full py-2 text-[10px] font-bold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
              >
                View All Student Insights
              </button>
            </div>




            {/* Faculty Hours Allotment - Fluid */}
            <div className="xl:col-span-1 p-6 h-[480px] rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm flex flex-col transition-all hover:shadow-md">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-bold">Faculty Allotment</h3>
                </div>
                <select
                  value={allotmentHorizon}
                  onChange={(e) => setAllotmentHorizon(e.target.value)}
                  className="text-[10px] h-7 w-28 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-2 pr-6 focus:outline-none focus:ring-2 focus:ring-blue-500/10 transition-all cursor-pointer font-bold"
                >
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                  <option value="weekly_avg">Weekly (Avg)</option>
                  <option value="total">Total</option>
                </select>
              </div>

               <ScrollArea className="flex-1 pr-2">
                <div className="space-y-5">
                  {(() => {
                    const maxAllotted = Math.max(...facultyHours.map(f => f.allotted), 1);
                    return facultyHours.length > 0 ? (
                      facultyHours.map((faculty, i) => (
                        <ProgressRow
                          key={i}
                          label={faculty.name}
                          value={`${Number(faculty.actual.toFixed(1))}h / ${Number(faculty.allotted.toFixed(1))}h`}
                          color={['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899'][i % 5]}
                          pct={faculty.pct}
                          trackPct={(faculty.allotted / maxAllotted) * 100}
                        />
                      ))
                    ) : (
                      <div className="h-40 flex items-center justify-center border-2 border-dashed border-zinc-100 dark:border-zinc-800 rounded-xl">
                        <p className="text-[10px] text-zinc-400 font-medium italic">No faculty hours allotted</p>
                      </div>
                    );
                  })()}
                </div>
              </ScrollArea>
            </div>


            {/* ── Inquiry Analytics ─────────────────────────────── */}
            {/* Inquiries vs Tags - Fluid */}
            <div className="xl:col-span-4 p-8 h-[480px] rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm flex flex-col transition-all hover:shadow-md">
              <div className="flex items-center gap-2 mb-6">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <h3 className="text-sm font-bold">Inquiries vs Tags</h3>
              </div>
              {(() => {
                const counts = inquiryStats.tag_counts;
                const totalInq = Object.values(counts).reduce((a, b) => a + b, 0);
                const total = totalInq || 1;
                const bars = [
                  { key: 'positive', label: 'Positive', color: '#10b981', icon: ThumbsUp, value: counts.positive || 0 },
                  { key: 'convincing', label: 'Convincing', color: '#f59e0b', icon: Lightbulb, value: counts.convincing || 0 },
                  { key: 'completed', label: 'Completed', color: '#3b82f6', icon: CheckCircle2, value: counts.completed || 0 },
                ];

                if (totalInq === 0) {
                  return (
                    <div className="flex-1 flex flex-col items-center justify-center text-zinc-400 italic">
                      <MessageSquare className="w-8 h-8 mb-2 opacity-20" />
                      <p className="text-[10px]">No inquiries logged yet</p>
                    </div>
                  );
                }

                return (
                  <div className="flex-1 flex gap-4 mt-4 h-full min-h-0">
                    {/* Y-Axis Ticks */}
                    <div className="flex flex-col justify-between h-full pb-2 text-[9px] text-zinc-400 font-bold border-r border-zinc-100 dark:border-zinc-800/50 pr-3 tabular-nums shrink-0">
                      {[1, 0.75, 0.5, 0.25, 0].map((step, i) => (
                        <span key={i} className="flex items-center justify-end gap-1">
                          {Math.round(total * step)}
                          <div className="w-1 h-[1px] bg-zinc-200 dark:border-zinc-700" />
                        </span>
                      ))}
                    </div>

                    {/* Chart Area */}
                    <div className="flex-1 min-w-0 flex items-end justify-around gap-2 h-full px-4 border-b border-zinc-100 dark:border-zinc-800 pb-2 relative">
                      <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-2 pr-2">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="w-full border-t border-zinc-50 dark:border-zinc-800/30 border-dashed" />
                        ))}
                      </div>

                      {bars.map((bar, i) => (
                        <div key={bar.key} className="flex-1 min-w-0 flex flex-col items-center gap-3 group relative h-full justify-end z-10">
                          <div className="absolute -top-6 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-zinc-900 border border-zinc-700 text-white text-[10px] px-2 py-1 rounded-md shadow-2xl pointer-events-none z-30">
                            <span className="font-bold">{bar.value}</span> <span className="opacity-70 text-[8px]">INQUIRIES</span>
                          </div>
                          <div
                            className="w-full max-w-[40px] rounded-t-lg transition-all duration-700 min-h-[2px]"
                            style={{ height: `${(bar.value / total) * 100}%`, backgroundColor: bar.color }}
                          />
                          <div className="flex flex-col items-center gap-1 w-full">
                            <bar.icon className="w-3 h-3 text-zinc-400 group-hover:text-zinc-600 transition-colors" />
                            <span className="text-[10px] font-bold text-zinc-400 group-hover:text-zinc-600 transition-colors uppercase tracking-tighter truncate w-full text-center block">
                              {bar.label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4 text-center">
                <span className="text-[10px] text-zinc-400">Total inquiries: <span className="font-bold text-zinc-600 dark:text-zinc-300">{Object.values(inquiryStats.tag_counts).reduce((a, b) => a + b, 0)}</span></span>
              </div>
            </div>



          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
